import { ComponentProps } from 'react';
import { Group, Stack } from '@mantine/core';
import { Table as TableType } from '@tanstack/table-core';
import { PageSelect } from './PageSelect';
import { cva, VariantProps } from 'class-variance-authority';
import styles from './Footer.module.css';
import { Select } from 'components/select/Select';
import { Text } from 'components/text/Text';
import { ActionIcon } from 'components/action-icon/ActionIcon';
import { ChevronRight, ChevronLeftIcon } from 'icons';
import { FirstIcon } from 'icons/FirstIcon';
import { LastIcon } from 'icons/LastIcon';

const footer = cva(styles.base, {
    variants: {
        appearance: {
            normal: styles.normal,
            minimal: styles.minimal,
        },
        position: {
            sticky: styles.stickyPosition,
            default: undefined,
        },
    },
    defaultVariants: {
        appearance: 'normal',
        position: 'default',
    },
});

const rightSection = cva(styles.rightSectionBase, {
    variants: {
        appearance: {
            normal: styles.rightSectionNormal,
            minimal: styles.rightSectionMinimal,
        },
    },
});

const rightSectionGroup = cva(styles.rightSectionGroupBase, {
    variants: {
        appearance: {
            normal: styles.rightSectionGroupNormal,
            minimal: styles.rightSectionGroupMinimal,
        },
    },
});

export type FooterPosition = VariantProps<typeof footer>['position'];

export const Footer = ({
    table,
    pageSizeOptions,
    isDisabled,
    position,
    footerAppearance = 'normal',
}: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    table: TableType<any>;
    pageSizeOptions: number[];
    isDisabled?: boolean;
    footerAppearance?: 'normal' | 'minimal';
    position?: FooterPosition;
}) => {
    const pageSize = table.getState().pagination.pageSize;
    const numberOfPages = table.getPageCount();
    const numberOfItems = table.getRowCount();
    const page = table.getState().pagination.pageIndex;
    const pageForDisplay = page + 1;

    const itemRangeLow = numberOfItems === 0 ? numberOfItems : pageForDisplay * pageSize - pageSize + 1;
    const itemRangeHigh = Math.min(pageForDisplay * pageSize, numberOfItems);

    const onChange: ComponentProps<typeof PageSelect>['onChange'] = value =>
        table.setPageIndex(parseInt(value));

    const textColor = 'gray.5';

    return (
        <Group classNames={{ root: footer({ appearance: footerAppearance, position }) }}>
            {footerAppearance === 'normal' && (
                <Group h={'100%'} className={styles.sizeNormal}>
                    <Group gap={'8px'} px={'12px'}>
                        <Text size={'sm'} c={textColor}>
                            Items per page:
                        </Text>
                        <Select
                            size="xs"
                            classNames={{ input: styles.input }}
                            onChange={value => {
                                if (typeof value === 'string') {
                                    const pageSize = parseInt(value);
                                    table.setPageSize(pageSize);
                                }
                            }}
                            value={pageSize.toString()}
                            disabled={isDisabled}
                            data={pageSizeOptions.map(pageSizeOption => pageSizeOption.toString())}
                            aria-label="Select Items per page"
                        />
                    </Group>
                    <Stack classNames={{ root: styles.items }}>
                        <Text size={'sm'} c={textColor} px={'12px'}>
                            {itemRangeLow}–{itemRangeHigh} of {numberOfItems} items
                        </Text>
                    </Stack>
                </Group>
            )}
            <Group classNames={{ root: rightSectionGroup({ appearance: footerAppearance }) }}>
                <Group classNames={{ root: rightSection({ appearance: footerAppearance }) }}>
                    <Text size={'sm'} c={textColor}>
                        Page:
                    </Text>
                    <PageSelect
                        onChange={onChange}
                        pageIndex={page}
                        numberOfPages={numberOfPages}
                        isDisabled={isDisabled || numberOfPages <= 1}
                    />
                    <Text truncate size={'sm'} c={textColor}>
                        of {numberOfPages}
                    </Text>
                </Group>
                <Group classNames={{ root: rightSection({ appearance: footerAppearance }) }}>
                    <ActionIcon
                        classNames={{ root: styles.toStartAndToEnd }}
                        intent={'neutral'}
                        appearance={'outline'}
                        disabled={!table.getCanPreviousPage() || isDisabled}
                        onClick={table.firstPage}
                        aria-label="Go to first page"
                    >
                        <FirstIcon />
                    </ActionIcon>
                    <ActionIcon
                        intent={'neutral'}
                        appearance={'outline'}
                        disabled={!table.getCanPreviousPage() || isDisabled}
                        onClick={table.previousPage}
                        aria-label="Go to previous page"
                    >
                        <ChevronLeftIcon />
                    </ActionIcon>
                    <ActionIcon
                        intent={'neutral'}
                        appearance={'outline'}
                        disabled={!table.getCanNextPage() || isDisabled}
                        onClick={table.nextPage}
                        aria-label="Go to next page"
                    >
                        <ChevronRight />
                    </ActionIcon>
                    <ActionIcon
                        classNames={{ root: styles.toStartAndToEnd }}
                        intent={'neutral'}
                        appearance={'outline'}
                        disabled={!table.getCanNextPage() || isDisabled}
                        onClick={table.lastPage}
                        aria-label="Go to last page"
                    >
                        <LastIcon />
                    </ActionIcon>
                </Group>
            </Group>
        </Group>
    );
};
